import { React, useState, useEffect } from "react";
import { connect } from "react-redux";
import { Auth, Hub } from "aws-amplify";
import QRCode from "react-qr-code";
import ClipLoader from "react-spinners/ClipLoader";
import { setUserData, getUserData } from "../../Actions/UserDataActions";
import {
	beginLoginMfaSetup,
	confirmLoginMfa,
	loginUser,
	verifyLoginMfaSetup,
} from "../../Services/Auth/AuthAPI";
import { MESSAGES } from "../../Utils/ErrorMessages";
import { useNavigate } from "react-router-dom";
import Container from "../../Layouts/Common/Container/Container";
import { AuthenticatedBuyerUserHomepage, AuthenticatedSupplierUserHomepage, SupplierCognitoGroup, FederatedIDPProvider } from "../../Utils/Constants";
import { doesUserHasResourceAccess } from '../../Utils/HelperFunctions'
// import "./Login.css";

const Login = (props) => {
	const navigate = useNavigate();
	const { setUserData } = props;

	const [user, setUser] = useState({
		username: null,
		password: null,
		isValid: false,
	});

	

	const [error, Seterrormsg] = useState({
		username: null,
		password: null,
		mfaCode: null,
		authErr: null,
	});

	const [mfaUser, setMfaUser] = useState(null);
	const [mfaCode, setMfaCode] = useState("");
	const [mfaSecretCode, setMfaSecretCode] = useState("");
	const [authStep, setAuthStep] = useState("LOGIN");
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		localStorage.clear()
	}, [])


	useEffect(() => {
		const removeListener = Hub.listen("auth", (data) => {
			switch (data.payload.event) {
				case "signIn":
					localStorage.setItem("cognitoUser", "true");
					localStorage.setItem("isAuthenticated", "true");
					Auth.currentSession().then((response) => {
						localStorage.setItem(
							"userData",
							JSON.stringify(response.idToken.payload)
						);
						setUserData({ ...response.idToken.payload, isAuthenticated: true })
						if (doesUserHasResourceAccess(SupplierCognitoGroup)) {
							navigate(AuthenticatedSupplierUserHomepage);
						} else {
							navigate(AuthenticatedBuyerUserHomepage);
						}
					});
					setMfaUser(null);
					setMfaCode("");
					setMfaSecretCode("");
					setAuthStep("LOGIN");
					break;
				case "signIn_failure":
					break;
				default:
					break;
			}
		});

		return removeListener;
	}, [navigate, setUserData]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (validateUser()) {
			setIsLoading(true);
			try {
				const loginResponse = await loginUser(user.username, user.password);

				if (loginResponse?.challengeName === "SOFTWARE_TOKEN_MFA") {
					setMfaUser(loginResponse);
					setMfaCode("");
					setMfaSecretCode("");
					setAuthStep("MFA_VERIFY");
					Seterrormsg({
						username: null,
						password: null,
						mfaCode: null,
						authErr: null,
					});
					return;
				}

				if (loginResponse?.challengeName === "MFA_SETUP") {
					const secretCode = await beginLoginMfaSetup(loginResponse);
					setMfaUser(loginResponse);
					setMfaSecretCode(secretCode);
					setMfaCode("");
					setAuthStep("MFA_SETUP");
					Seterrormsg({
						username: null,
						password: null,
						mfaCode: null,
						authErr: null,
					});
					return;
				}
			} catch (e) {
				Seterrormsg({
					username: null,
					password: null,
					mfaCode: null,
					authErr: getAuthErrorMessage(e),
				});
			} finally {
				setIsLoading(false);
			}
		}
	};

	const handleMfaSubmit = async (e) => {
		e.preventDefault();

		if (!mfaCode) {
			Seterrormsg((prevState) => ({
				...prevState,
				mfaCode: "Authenticator code is required",
				authErr: null,
			}));
			return;
		}

		setIsLoading(true);
		try {
			if (authStep === "MFA_SETUP") {
				const setupResponse = await verifyLoginMfaSetup(mfaUser, mfaCode);

				if (setupResponse?.challengeName === "SOFTWARE_TOKEN_MFA") {
					setMfaUser(setupResponse);
					setMfaCode("");
					setMfaSecretCode("");
					setAuthStep("MFA_VERIFY");
					Seterrormsg({
						username: null,
						password: null,
						mfaCode: null,
						authErr: "Authenticator app linked. Enter the latest code to finish signing in.",
					});
					return;
				}

				if (setupResponse) {
					return;
				}

				setMfaUser(null);
				setMfaCode("");
				setMfaSecretCode("");
				setAuthStep("LOGIN");
				Seterrormsg({
					username: null,
					password: null,
					mfaCode: null,
					authErr:
						"Authenticator app linked successfully. Please sign in again with a fresh code.",
				});
				return;
			}

			await confirmLoginMfa(mfaUser, mfaCode);
			Seterrormsg({
				username: null,
				password: null,
				mfaCode: null,
				authErr: null,
			});
		} catch (e) {
			Seterrormsg((prevState) => ({
				...prevState,
				mfaCode: null,
				authErr: getAuthErrorMessage(e),
			}));
		} finally {
			setIsLoading(false);
		}
	};

	const FederatedLogin = () => {
		localStorage.setItem("idpLogin", true);
		Auth.federatedSignIn({ provider: FederatedIDPProvider });
	};

	function onHandleChange(e, name) {
		let userObj = { ...user };
		userObj[name] = e.target.value;
		setUser(userObj);
		Seterrormsg((prevState) => ({
			...prevState,
			[name]: null,
			authErr: null,
		}));
	}

	function onHandleMfaChange(e) {
		setMfaCode(e.target.value);
		Seterrormsg((prevState) => ({
			...prevState,
			mfaCode: null,
			authErr: null,
		}));
	}

	function resetMfaState() {
		setIsLoading(false);
		setMfaUser(null);
		setMfaCode("");
		setMfaSecretCode("");
		setAuthStep("LOGIN");
		Seterrormsg((prevState) => ({
			...prevState,
			mfaCode: null,
			authErr: null,
		}));
	}

	function getAuthErrorMessage(e) {
		if (e?.code === "UserNotConfirmedException") {
			return "Your account is not confirmed yet. Please complete the registration confirmation step.";
		}

		if (e?.code === "CodeMismatchException" || e?.code === "ExpiredCodeException") {
			return "Invalid authenticator code. Please enter the latest code from your authenticator app.";
		}

		if (
			e?.code === "NotAuthorizedException" ||
			e?.code === "UserNotFoundException"
		) {
			return MESSAGES.INVALID_CREDENTIALS;
		}

		return e?.message || "Unable to sign in right now.";
	}

	function getTotpSetupUri() {
		if (!mfaSecretCode || !user?.username) {
			return "";
		}

		const issuer = "Williams Lea Procurement Portal";
		const label = `${issuer}:${user.username}`;

		return `otpauth://totp/${encodeURIComponent(label)}?secret=${encodeURIComponent(
			mfaSecretCode
		)}&issuer=${encodeURIComponent(issuer)}`;
	}

	function validateUser() {
		let isValid = true;
		let userObj = { ...user };
		if (!userObj.username && !userObj.password) {
			Seterrormsg({
				username: MESSAGES.EMAIL_REQUIRED,
				password: MESSAGES.PASSWORD_REQUIRED,
				mfaCode: null,
				authErr: null,
			});
			isValid = false;
		} else if (!userObj.username) {
			Seterrormsg({
				username: MESSAGES.EMAIL_REQUIRED,
				password: null,
				mfaCode: null,
				authErr: null,
			});
			isValid = false;
		} else if (!MESSAGES.PATTERN.test(userObj.username)) {
			Seterrormsg({
				username: MESSAGES.INVALID_EMAIL,
				password: null,
				mfaCode: null,
				authErr: null,
			});
			isValid = false;
		} else if (!userObj.password) {
			Seterrormsg({
				username: null,
				password: MESSAGES.PASSWORD_REQUIRED,
				mfaCode: null,
				authErr: null,
			});
			isValid = false;
		}
		return isValid;
	}

	return (
		<Container pageName={"Login"}>
			<div className="row">
				<div className="col-md-5">
					<p className="Suppliersheading">Suppliers</p>
					
					<form className={`loginForm ${authStep !== "LOGIN" ? "mfaLoginForm" : ""}`}>
						{authStep === "LOGIN" ? (
							<>
								<div className="form-group loginLabel" id="loginLabel">
									<div htmlFor="Email" className="required emailLabel">
										Email
									</div>
									<input
										type="email"
										className="form-control w-100"
										onChange={(e) => onHandleChange(e, "username")}
										disabled={isLoading}
										required
									/>
									{error.username && <small className="text-danger">{error.username}</small>}
									<br />
								</div>
								
								<div className="form-group loginLabel" id="loginLabel">
									<div htmlFor="Password" className="required">
										Password
									</div>
									<input
										type="password"
										className="form-control"
										onChange={(e) => onHandleChange(e, "password")}
										disabled={isLoading}
										required
									/>
									{error.password && <small className="text-danger">{error.password}</small>}
								</div>
								
								<p id="ForgetloginLabel"><a href="forgot-password" className="forgetPasswordLink">Forgot password ?</a></p>
								
								{error.authErr && <p className="text-danger authErrorMessage">{error.authErr}</p>}
								
								<div id="loginButton">
									<button type="button" className="Loginbutton" onClick={handleSubmit} disabled={isLoading}>
										<span className="loginButtonContent">
											{isLoading && (
												<ClipLoader
													color={"#ffffff"}
													size={18}
													aria-label="Loading Spinner"
													data-testid="login-loader"
												/>
											)}
											{isLoading ? "Loading" : "Login"}
										</span>
									</button>
									<p className="signUpTab"><span>Not a member?</span> <span ><a href="/signup" className="SignUpLink">Sign-Up</a></span></p>
								</div>
							</>
						) : (
							<>
								<div className="form-group loginLabel mfaPanel" id="loginLabel">
									{authStep === "MFA_SETUP" && (
										<div className="mfaQrSection">
											<h3 className="mfaStepTitle">Set up your authenticator app</h3>
											<div className="mfaQrWrapper">
												<div className="mfaQrCard">
													<QRCode
														value={getTotpSetupUri()}
														size={148}
														bgColor="#FFFFFF"
														fgColor="#000000"
													/>
												</div>
											</div>
											<p className="mfaQrHelpText">
												Open Google Authenticator or Microsoft Authenticator and scan this QR code.
											</p>
										</div>
									)}
									<label className="mfaInputLabel" htmlFor="mfaCodeInput">
										Verification Code
									</label>
									<input
										id="mfaCodeInput"
										type="text"
										className="form-control mfaCodeInput"
										value={mfaCode}
										onChange={onHandleMfaChange}
										disabled={isLoading}
										inputMode="numeric"
										autoComplete="one-time-code"
										required
									/>
									<small className="form-text text-muted mfaInputHelpText">
										{authStep === "MFA_SETUP"
											? "After adding the account,please enter the 6-digit code from your authenticator app."
											: "Please enter the 6-digit code from your authenticator app."}
									</small>
									{error.mfaCode && <small className="text-danger d-block">{error.mfaCode}</small>}
								</div>
								{error.authErr && <p className="text-danger authErrorMessage">{error.authErr}</p>}
								<div id="loginButton">
									<button type="button" className="Loginbutton" onClick={handleMfaSubmit} disabled={isLoading}>
										<span className="loginButtonContent">
											{isLoading && (
												<ClipLoader
													color={"#ffffff"}
													size={18}
													aria-label="Loading Spinner"
													data-testid="mfa-loader"
												/>
											)}
											{isLoading
												? "Loading"
												: authStep === "MFA_SETUP"
													? "Link authenticator app"
													: "Verify code"}
										</span>
									</button>
									<button
										type="button"
										className="secondaryLoginButton"
										onClick={resetMfaState}
										disabled={isLoading}
									>
										Back to login
									</button>
								</div>
							</>
						)}
					</form>
				</div>
				<div className="col-md-2">
					<div className="vertical"></div>
				</div>
				<div className="col-md-5">
					<p className="Buyersheading">Buyers</p>
					
					<div className="intro">
						<p>Please use your organisation account to access Williams lea
						Procurement portal </p>
						<p>
						To Change or reset your password of your organisation account,please
						contact your system administrator or helpdesk
						</p>
						
					</div>
				
					<div className="loginButton">
						<button type="button" onClick={FederatedLogin} className="buyerLoginbutton">
							Login via Organisation
						</button>
					</div>
				</div>
			</div>
		</Container>
	);
};
const mapStateToProps = (state) => {
	return { ...state.userData };
};

const mapDispatchToProps = (dispatch) => {
	return {
		setUserData: (data) => {
			dispatch(setUserData(data));
		},
		getUserData: () => {
			dispatch(getUserData());
		},
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
